import { Row, RowSelectionState } from '@tanstack/react-table';
import { getRelativePosition } from 'chart.js/helpers';
import { OrderEntryTableRow } from '../aggregated-order-table/columns';
import { SegmentClickHandler, LabelClickHandler, CustomDataset } from './types';
import { ChakraColor, hexToRgb } from 'src/utils/color-util';

// Helper function to parse a value to a number
export const parseNumericValue = (value: unknown): number => {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    // Attempt to remove formatting and parse, e.g., "1,234.56 units" -> 1234.56
    const num = parseFloat(value.replace(/[^0-9.-]/g, ''));
    return isNaN(num) ? 0 : num;
  }
  // Check if it's an object like { value: number }
  if (
    typeof value === 'object' &&
    value !== null &&
    'value' in value &&
    typeof value.value === 'number'
  ) {
    return value.value;
  }
  return 0;
};

// Helper function for recursive row selection
export const getRecursiveSelection = (
  parent: Row<OrderEntryTableRow>,
  select = true
): Array<[string, boolean]> => {
  return [
    [parent.id, select],
    ...parent.subRows.flatMap((childRow) =>
      getRecursiveSelection(childRow, select)
    ),
  ];
};

// Helper function to handle segment clicks
export const handleSegmentClick: SegmentClickHandler = (
  activeElements,
  chart,
  table,
  setRowSelection,
  shiftPressed
) => {
  const clickedElement = activeElements[0];
  const datasetIndex = clickedElement.datasetIndex;
  const dataIndex = clickedElement.index;

  const dataset = chart.data.datasets[datasetIndex] as CustomDataset;
  const clickedSubRow = dataset?.meta?.subRows?.[dataIndex];

  if (clickedSubRow) {
    const selectedRowIds = table.getState().rowSelection;
    const isCurrentlySelected = selectedRowIds[clickedSubRow.id] ?? false;

    if (shiftPressed) {
      const newSelectionState = !isCurrentlySelected;
      const entriesToToggle = Object.fromEntries(
        getRecursiveSelection(clickedSubRow, newSelectionState)
      );
      setRowSelection((prev) => ({ ...prev, ...entriesToToggle }));
    } else {
      const entriesToSelect = Object.fromEntries(
        getRecursiveSelection(clickedSubRow, true)
      );
      setRowSelection(entriesToSelect);
    }
  }
};

// Helper function to handle label/empty area clicks
export const handleLabelClick: LabelClickHandler = (
  event,
  chart,
  table,
  setRowSelection,
  shiftPressed
) => {
  const canvasPosition = getRelativePosition(event, chart);
  const dataX = chart.scales.x.getValueForPixel(canvasPosition.x);

  if (dataX !== null && dataX !== undefined && dataX >= 0) {
    const dataIndex = Math.round(dataX);
    const tableRows = table.getRowModel().rows;

    if (dataIndex < tableRows.length) {
      const parentRow = tableRows[dataIndex];
      const isCurrentlySelected =
        table.getState().rowSelection[parentRow.id] ?? false;

      if (shiftPressed) {
        if (isCurrentlySelected) {
          const entriesToRemove = Object.fromEntries(
            getRecursiveSelection(parentRow).map(([id]) => [id, false])
          );
          setRowSelection((prev) => ({ ...prev, ...entriesToRemove }));
        } else {
          const entriesToAdd = Object.fromEntries(
            getRecursiveSelection(parentRow)
          );
          setRowSelection((prev) => ({ ...prev, ...entriesToAdd }));
        }
      } else {
        const entries = Object.fromEntries(getRecursiveSelection(parentRow));
        setRowSelection(entries);
      }
    }
  }
};

// Helper function to get background colors based on selection
export const getBackgroundColors = (
  rows: Row<OrderEntryTableRow>[],
  rowSelection: RowSelectionState,
  defaultColor: string = ChakraColor.gray500
): string[] => {
  return rows.map((row) => {
    const color = row.original.color ?? defaultColor;
    const [r, g, b] = hexToRgb(color);
    const isSelected = rowSelection[row.id] ?? false;
    const alpha = isSelected ? 1.0 : 0.3;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  });
};

// Helper function to check if all sub-rows are selected
export const areAllSubRowsSelected = (
  parentRow: Row<OrderEntryTableRow>,
  rowSelection: RowSelectionState
): boolean => {
  return (
    parentRow.subRows.length > 0 &&
    parentRow.subRows.every((subRow) => rowSelection[subRow.id] ?? false)
  );
};
